import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, ArrowRight, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";

const AUD = new Intl.NumberFormat("en-AU");

/**
 * Feb 2026 — Partner Notification Health tile.
 * Fetches `/api/admin/partner-notify-health/summary?window=7d` and
 * renders counters + a highlight when there are unresolved failures.
 * Clicking the tile lands on the full drill-down at
 * `/admin/partner-notify-health`.
 */
export default function PartnerNotifyHealthTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/admin/partner-notify-health/summary?window=7d")
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const needsAttention = !!data?.needs_attention;
  const noData = !!data?.no_data_in_window;
  // F-6 tile — three states:
  //  • needsAttention → amber warning
  //  • noData (nothing has run this window) → slate/grey — never green
  //  • otherwise → healthy green
  let wrapClass = "border-slate-200 bg-white";
  if (needsAttention) {
    wrapClass = "border-amber-300 bg-gradient-to-br from-amber-50 to-white";
  } else if (noData) {
    wrapClass = "border-slate-300 bg-gradient-to-br from-slate-50 to-white";
  }

  return (
    <Link
      to="/admin/partner-notify-health"
      data-testid="kpi-tile-partner-notify-health"
      className={`group relative rounded-2xl border ${wrapClass} p-5 hover:shadow-md transition`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BellRing className="w-4 h-4 text-slate-500" />
          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
            Partner report notifications · 7d
          </div>
        </div>
        {loading ? <Loader2 className="w-4 h-4 text-slate-400 animate-spin" /> :
          needsAttention ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5" data-testid="notify-health-badge-attention">
              <AlertTriangle className="w-3 h-3" /> Needs attention
            </span>
          ) : noData ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300 rounded-full px-2 py-0.5" data-testid="notify-health-badge-no-data">
              No activity yet
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="notify-health-badge-healthy">
              <CheckCircle2 className="w-3 h-3" /> All healthy
            </span>
          )
        }
      </div>

      <div className="mt-4 flex items-baseline gap-3 flex-wrap">
        <div>
          <div className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="notify-health-sent">
            {AUD.format(data?.sent ?? 0)}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Sent</div>
        </div>
        <div className="pl-4 border-l border-slate-200">
          <div className="text-2xl font-bold text-slate-700 tabular-nums" data-testid="notify-health-opened">
            {AUD.format(data?.opened ?? 0)}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Opened {data?.sent ? `(${Math.round((data.open_rate || 0) * 100)}%)` : ""}
          </div>
        </div>
        <div className="pl-4 border-l border-slate-200">
          <div className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="notify-health-clicked">
            {AUD.format(data?.clicked ?? 0)}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Clicked {data?.sent ? `(${Math.round((data.click_rate || 0) * 100)}%)` : ""}
          </div>
        </div>
        <div className="pl-4 border-l border-slate-200">
          <div className={`text-2xl font-bold tabular-nums ${(data?.failed ?? 0) > 0 ? "text-red-700" : "text-slate-400"}`} data-testid="notify-health-failed">
            {AUD.format(data?.failed ?? 0)}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Failed</div>
        </div>
        <div className="pl-4 border-l border-slate-200">
          <div className="text-2xl font-bold text-slate-500 tabular-nums" data-testid="notify-health-skipped">
            {AUD.format(data?.skipped ?? 0)}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Skipped</div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        <div className="text-slate-500">
          {noData
            ? <span className="text-slate-600">No partner report emails have run in this window yet</span>
            : data?.stale_over_6h
              ? <span className="text-amber-800">One or more failures &gt; 6h old</span>
              : "All attempts within the healthy window"}
        </div>
        <span className="inline-flex items-center gap-1 font-semibold text-slate-700 group-hover:translate-x-0.5 transition-transform">
          Open drill-down <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}
